import { router, useForm } from "@inertiajs/react";
import { ChevronDown, Search, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import AppLayout from "../../layouts/AppLayout";

// ── SearchableCombobox ────────────────────────────────────────────────────────
// Inline — same pattern as JobAlerts/Index.tsx. Extract to a shared component
// once a third caller exists.
interface ComboboxOption { value: string; label: string; group?: string }

function SearchableCombobox({ value, onChange, options, allLabel }: {
  value: string;
  onChange: (v: string) => void;
  options: ComboboxOption[];
  allLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.group ?? "").toLowerCase().includes(q));
  }, [options, query]);

  const selectedLabel = value === "all" ? null : (options.find((o) => o.value === value)?.label ?? value);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 text-sm border rounded-lg px-2.5 py-1.5 bg-white transition-colors ${
          value !== "all"
            ? "border-green-400 text-green-700"
            : "border-gray-200 text-gray-600 hover:border-gray-300"
        }`}
      >
        <span className="max-w-[160px] truncate">{selectedLabel ?? allLabel}</span>
        {value !== "all" ? (
          <X className="w-3.5 h-3.5 flex-shrink-0 opacity-60 hover:opacity-100"
            onClick={(e) => { e.stopPropagation(); onChange("all"); setOpen(false); }} />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
        )}
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 flex flex-col" style={{ maxHeight: "280px" }}>
          <div className="p-2 border-b border-gray-100 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
              placeholder="Search..."
              className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-green-400"
            />
          </div>
          <div className="overflow-y-auto">
            <button type="button" onClick={() => { onChange("all"); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${value === "all" ? "text-green-700 font-medium bg-green-50" : "text-gray-500 hover:bg-gray-50"}`}>
              {allLabel}
            </button>
            {filtered.map((opt) => (
              <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${value === opt.value ? "text-green-700 font-medium bg-green-50" : "text-gray-700 hover:bg-gray-50"}`}>
                {opt.label}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

interface Candidate {
  id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
  location: string;
  jd_year: number;
  pipeline_stage: string;
  priority: string;
  source: string;
  days_in_stage: number;
  email: string;
  lifecycle_state: string;
}

interface Filters {
  q?: string;
  stage?: string;
  priority?: string;
  lifecycle?: string;
  practice_area?: string;
  location?: string;
}

interface Props {
  candidates: Candidate[];
  filters: Filters;
  practice_area_options: string[];
  location_options: string[];
}

const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead",
  intake_scheduled: "Intake Scheduled",
  resume_editing: "Resume Editing",
  package_pending: "Package Pending",
  submitted: "Submitted",
  screener: "Screener",
  full_round: "Full Round",
  offer_placed: "Offer / Placed",
};

const PRIORITY_BADGE: Record<string, string> = {
  high:   "bg-red-100 text-red-700",
  normal: "bg-gray-100 text-gray-600",
  low:    "bg-blue-50 text-blue-600",
};

const LIFECYCLE_TABS = ["active", "inactive", "archived", "all"] as const;
const LIFECYCLE_LABELS: Record<string, string> = {
  active: "Active", inactive: "Inactive", archived: "Archived", all: "All",
};

const LIFECYCLE_BADGE: Record<string, string> = {
  inactive: "bg-yellow-100 text-yellow-700",
  archived: "bg-red-100 text-red-600",
};

export default function CandidatesIndex({ candidates, filters, practice_area_options, location_options }: Props) {
  const [searchQ, setSearchQ] = useState(filters.q ?? "");
  const importForm = useForm<{ file: File | null }>({ file: null });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const lifecycle = filters.lifecycle ?? "active";

  const practiceOptions = useMemo(
    () => practice_area_options.map((pa) => ({ value: pa, label: pa })),
    [practice_area_options]
  );
  const locationOptions = useMemo(
    () => location_options.map((loc) => ({ value: loc, label: loc })),
    [location_options]
  );

  const applyFilter = (key: string, value: string) => {
    // Strip blank/null values from the merged params. Spreading ...filters
    // otherwise re-emits the OTHER filters' empty values as `?key=` params,
    // which round-trip back as "" and re-render a cleared combobox as a green
    // pill — the clear-one-box-re-greens-the-other ping-pong.
    const merged: Record<string, string | undefined> = { ...filters, lifecycle, [key]: value || undefined };
    const clean = Object.fromEntries(
      Object.entries(merged).filter(([, v]) => v != null && v !== "")
    );
    router.get("/candidates", clean, {
      preserveState: true,
      replace: true,
    });
  };

  const switchLifecycle = (tab: string) => {
    // Preserve text search across lifecycle switches; clear practice/location since options change.
    const params: Record<string, string> = { lifecycle: tab };
    if (filters.q) params.q = filters.q;
    if (filters.stage) params.stage = filters.stage;
    if (filters.priority) params.priority = filters.priority;
    router.get("/candidates", params, { replace: true });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    applyFilter("q", searchQ);
  };

  const clearFilters = () => {
    setSearchQ("");
    router.get("/candidates", {}, { replace: true });
  };

  const hasFilters = !!filters.q || !!filters.stage || !!filters.priority ||
                     lifecycle !== "active" || !!filters.practice_area || !!filters.location;

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    importForm.setData("file", file);
    importForm.post("/candidates/import", {
      forceFormData: true,
      onSuccess: () => { if (fileInputRef.current) fileInputRef.current.value = ""; },
    });
  };

  const rosterLabel =
    lifecycle === "active"   ? "Active candidates" :
    lifecycle === "inactive" ? "Inactive candidates" :
    lifecycle === "archived" ? "Archived candidates" : "All candidates";

  return (
    <AppLayout title="Candidates">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Candidates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {rosterLabel} · {candidates.length} shown
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
            title="Import candidates from a Leopard CSV export into your roster. For one-time cold-outreach lists, use Email Campaigns → Upload Outbound List instead."
          >
            <Upload size={12} /> Import Roster CSV
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          </label>
          <a
            href="/candidates/new"
            className="px-4 py-2 bg-green-700 text-white text-sm font-medium rounded-md hover:bg-green-800 transition-colors"
            title="Manually add a candidate to your roster (name, email, firm, practice area)."
          >
            Add Candidate
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {/* Lifecycle tabs */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {LIFECYCLE_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => switchLifecycle(tab)}
              className={`px-3 py-1.5 font-medium transition-colors ${
                lifecycle === tab ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {LIFECYCLE_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Text search */}
        <form onSubmit={submitSearch} className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search name, email, firm…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </form>

        {/* Stage */}
        <select
          value={filters.stage ?? ""}
          onChange={(e) => applyFilter("stage", e.target.value)}
          className="text-sm border border-gray-200 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-600"
        >
          <option value="">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        {/* Priority */}
        <select
          value={filters.priority ?? ""}
          onChange={(e) => applyFilter("priority", e.target.value)}
          className="text-sm border border-gray-200 rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-600"
        >
          <option value="">All Priorities</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>

        {/* Practice area */}
        {practiceOptions.length > 0 && (
          <SearchableCombobox
            value={filters.practice_area || "all"}
            onChange={(v) => applyFilter("practice_area", v === "all" ? "" : v)}
            options={practiceOptions}
            allLabel="All Practice Areas"
          />
        )}

        {/* Location */}
        {locationOptions.length > 0 && (
          <SearchableCombobox
            value={filters.location || "all"}
            onChange={(v) => applyFilter("location", v === "all" ? "" : v)}
            options={locationOptions}
            allLabel="All Locations"
          />
        )}

        {hasFilters && (
          <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        {candidates.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            {lifecycle === "active"
              ? "No active candidates yet. Use Import Roster CSV or Add Candidate to get started."
              : `No ${lifecycle === "all" ? "" : lifecycle + " "}candidates match the current filters.`}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Firm / Practice</th>
                <th className="text-left px-4 py-3 font-medium">Location</th>
                <th className="text-left px-4 py-3 font-medium">JD</th>
                <th className="text-left px-4 py-3 font-medium">Stage</th>
                <th className="text-left px-4 py-3 font-medium">Priority</th>
                <th className="text-right px-5 py-3 font-medium">Days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {candidates.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <a href={`/candidates/${c.id}`} className="font-medium text-gray-800 hover:text-green-700">
                        {c.full_name}
                      </a>
                      {c.lifecycle_state !== "active" && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${LIFECYCLE_BADGE[c.lifecycle_state] ?? ""}`}>
                          {c.lifecycle_state}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{c.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-700">{c.firm_name}</div>
                    <div className="text-xs text-gray-400">{c.practice_area}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{c.location}</td>
                  <td className="px-4 py-3 text-gray-500">{c.jd_year}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {STAGE_LABELS[c.pipeline_stage] ?? c.pipeline_stage}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PRIORITY_BADGE[c.priority] ?? ""}`}>
                      {c.priority}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right text-gray-400 text-xs">{c.days_in_stage}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  );
}
